/**
 * Direct-messaging (DM) data layer — backend #1199 (`/api/v1/dm/`).
 *
 * Mirrors the app's service convention: thin axios calls on the shared `request`
 * instance (auth + base URL handled there), exposed as react-query v3 hooks.
 *
 * Pagination matches the backend contract:
 *   - conversation list  → DRF LimitOffsetPagination ({ count, next, previous, results })
 *   - message history    → DRF CursorPagination       ({ next, previous, results }, newest-first)
 *
 * Writes (`send`, `mark-read`) invalidate the relevant queries so the list and
 * thread stay consistent (no manual refresh counters).
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";
import { v4 as uuidv4 } from "uuid";
import API from "./request";
import { CHAT_SEND_TIMEOUT_MS, CHAT_SEND_ATTACHMENT_TIMEOUT_MS } from "./chatSendTimeout";
import { API_PATH, QUERY_KEYS } from "../utils/config";
import {
  extractCursor,
  upsertOptimisticMessage,
  markMessageStatus,
  applyReactionToggle,
  patchMessageReactions,
  tombstoneConversationLastMessage,
  patchConversationMuted,
  patchConversationUnread,
} from "../pages/Chat/Chat.helpers";
import { buildWheelSharePayload } from "../pages/Chat/wheelSharePayload";

const CONVERSATIONS_LIMIT = 25;
const MESSAGES_PAGE_SIZE = 30;

/* ----- raw endpoints (axios) ----- */

export const fetchConversations = ({ offset = 0, limit = CONVERSATIONS_LIMIT } = {}) =>
  API.get(API_PATH.DM_CONVERSATIONS, {
    params: { offset, limit },
  }).then((res) => res.data);

export const fetchConversation = (conversationId) =>
  API.get(API_PATH.DM_CONVERSATION(conversationId)).then((res) => res.data);

export const fetchMessages = (conversationId, { cursor = null, pageSize = MESSAGES_PAGE_SIZE } = {}) =>
  API.get(API_PATH.DM_MESSAGES(conversationId), {
    params: { cursor: cursor || undefined, page_size: pageSize },
  }).then((res) => res.data);

/**
 * Send a message. `attachment` (D22 / GR-5, optional) is a raw `File` — when
 * present the request goes multipart (backend reads `request.FILES.attachment`,
 * moderates via chat.services.media_moderation BEFORE persist — see
 * messaging/api/v1/views.py `_gate_attachment`); a text-only send stays plain
 * JSON, byte-identical to before this feature.
 */
export const sendMessage = (conversationId, body, clientId, attachment) => {
  if (attachment) {
    const form = new FormData();
    form.append("body", body || "");
    if (clientId) form.append("client_id", clientId);
    form.append("attachment", attachment);
    return API.post(API_PATH.DM_MESSAGES(conversationId), form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: CHAT_SEND_ATTACHMENT_TIMEOUT_MS,
    }).then((res) => res.data);
  }
  return API.post(API_PATH.DM_MESSAGES(conversationId), {
    body,
    client_id: clientId,
  }, { timeout: CHAT_SEND_TIMEOUT_MS }).then((res) => res.data);
};

export const markConversationRead = (conversationId) =>
  API.post(API_PATH.DM_MARK_READ(conversationId), {});

/**
 * Mute / unmute a conversation (C4). Per-user: the server toggles ONLY the
 * caller's own `Participant.muted` and returns the confirmed state
 * (`{ data: { muted } }`), so the toggle binds to committed state, never a
 * fire-and-forget echo. Muting suppresses push / unread-badge escalation for
 * this user; it never changes what the peer sees.
 */
export const muteConversation = (conversationId) =>
  API.post(API_PATH.DM_MUTE(conversationId), {}).then((res) => res.data);

export const unmuteConversation = (conversationId) =>
  API.post(API_PATH.DM_UNMUTE(conversationId), {}).then((res) => res.data);

/**
 * Leave / delete a conversation (I6). Soft-leave: the server hides it from the
 * caller's list (and 404s it on open) while the peer keeps full history. Under
 * the default Facebook semantics a new peer message re-surfaces it. Danger
 * action — the caller confirms first (ConfirmBlockDialog pattern).
 */
export const leaveConversation = (conversationId) =>
  API.post(API_PATH.DM_LEAVE(conversationId), {}).then((res) => res.data);

/**
 * Edit an own message's body (1:1 polish). Server enforces sender-only
 * (403 for anyone else, incl. the other participant) and refuses an already
 * deleted message — see `messaging/api/v1/views.py` `MessageDetailView.patch`.
 */
export const editMessage = (messageId, body) =>
  API.patch(API_PATH.DM_MESSAGE_DETAIL(messageId), { body }).then(
    (res) => res.data
  );

/**
 * Self-delete an own message (tombstone, not a hard erase — thread shape is
 * kept; the server withholds `body`/`attachment` once `is_deleted` is true).
 * Idempotent server-side, so a double-click never errors.
 */
export const deleteMessage = (messageId) =>
  API.delete(API_PATH.DM_MESSAGE_DETAIL(messageId)).then((res) => res.data);

/**
 * BUG #919 — chat-message reactions (WhatsApp/emoji style). BE-BLOCKED: no
 * backend endpoint exists for these two calls (see the DM_MESSAGE_REACTIONS*
 * comment in utils/config.js) — they will 404 against today's API. Wired now,
 * against the FE's expected contract, so the feature lights up the moment BE
 * adds `POST/DELETE .../messages/{id}/reactions/` + broadcasts it over the DM
 * socket (chatSocket.js already forwards a `{type:"reaction"}` event to an
 * invalidate, ready for that addition). Until then, `useToggleMessageReaction`
 * below rolls the optimistic chip back on error exactly like SegmentReactions
 * does for the wheel/segment surface.
 */
export const addMessageReaction = (messageId, emoji) =>
  API.post(API_PATH.DM_MESSAGE_REACTIONS(messageId), { emoji }).then(
    (res) => res.data
  );

export const removeMessageReaction = (messageId, emoji) =>
  API.delete(API_PATH.DM_MESSAGE_REACTION_DETAIL(messageId, emoji)).then(
    (res) => res.data
  );

/**
 * ISP-9999 (Gordon #chatmedia-save): copy a media message's attachment into
 * the CALLER's own Media Library root (normal tagging, no special folder).
 * Any active participant may call this — including the sender re-saving
 * their own send — the server never mutates the original message/attachment,
 * only adds a new object under the caller's `uploads/<uid>/library/`.
 * 403 (`{ message }`) when the caller is at/over their storage cap — surfaced
 * by the caller as the message bubble's standard inline `MsgActionError`.
 */
export const saveMessageAttachmentToLibrary = (messageId) =>
  API.post(API_PATH.DM_MESSAGE_SAVE_TO_LIBRARY(messageId), {}).then(
    (res) => res.data
  );

/**
 * Get-or-create a 1:1 conversation with `recipientId`. The backend
 * (`POST /api/v1/dm/conversations/`, messaging #1199) is idempotent: it returns
 * the EXISTING 1:1 (200) or a freshly created one (201), so the caller never
 * has to look one up first. Response is the ConversationSerializer shape
 * ({ id, kind, other_participant, ... }); the caller navigates to
 * `/messages/{id}`. Server-side `can_message` (settings.DM_POLICY) still gates:
 * 400 self, 403 blocked/relationship, 404 recipient, 429 rate-limited, and the
 * whole endpoint 404s while CHAT_ENABLED is off.
 */
export const createConversation = (recipientId) =>
  API.post(API_PATH.DM_CONVERSATIONS, { recipient_id: recipientId }).then(
    (res) => res.data
  );

/**
 * ISP-9999 (kelly-0916-chat-not-following-notice-parity-with-gallery-search):
 * the ONE error-copy mapping for a failed conversation-CREATE attempt, shared
 * by every entry point that can start a new DM (useUserRelationship's
 * `openMessage` — gallery/profile/search "Message" buttons — and MiniDmDock's
 * "+" new-message compose picker). Both used to duplicate this switch with a
 * subtle divergence: MiniDmDock trusted the server's raw `detail` string
 * first, so on the (only currently possible, per `messaging/services/
 * policy.py`'s `can_interact` always-True until #18's Block model lands) 403
 * cause — the one-way-follow gate — it surfaced the blunt server string "You
 * are not allowed to message this user." instead of the actionable copy the
 * gallery-search entry point already showed for the identical failure
 * (MEASURED live web-dev, both via the same `POST /api/v1/dm/conversations/`
 * 403). `isFollowing` lets a caller that already knows the answer (gallery/
 * profile cards seed it from `useUserRelationship`) skip straight to the
 * friendly copy; callers that don't track per-row follow state (the compose
 * picker) simply omit it and get the same friendly copy, correct today because
 * conversation-CREATE's only live 403 cause is the follow gate.
 */
export const getStartConversationErrorMessage = (err, { isFollowing } = {}) => {
  const code = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (code === 403) {
    return isFollowing ? detail || "You can't message this user right now." : "Follow them first to message.";
  }
  if (code === 429) return "You're sending messages too fast. Please wait a moment.";
  if (code === 404) return "Messaging isn't available right now.";
  return detail || "Couldn't start the conversation. Please try again.";
};

/**
 * Share a wheel into an existing conversation as a chat message (ISP — "Share a
 * Wheel to a user via Chat"). Uses the ONE shared `buildWheelSharePayload` shape
 * (see pages/Chat/wheelSharePayload) so this and the in-chat wheel picker post an
 * identical `shared_wheel` structure.
 *
 * Goes through the SAME DM send seam as a normal message: the `body` carries a
 * plain-text name+link fallback that today's text-only backend stores and echoes
 * verbatim (so a shared wheel is always at least a readable, clickable message),
 * and `shared_wheel` is the forward-compatible rich-card field. NOTE (BE dep):
 * persisting + echoing `shared_wheel` so MessageBubble can render the wheel card
 * is a backend change — until it lands the shared wheel shows as the link body.
 */
export const shareWheelToConversation = (conversationId, wheel, clientId) => {
  const { body, shared_wheel } = buildWheelSharePayload(wheel);
  return API.post(API_PATH.DM_MESSAGES(conversationId), {
    body,
    client_id: clientId,
    shared_wheel,
  }).then((res) => res.data);
};

/* ----- react-query hooks ----- */

export const useConversations = (config = {}) =>
  useInfiniteQuery(
    [QUERY_KEYS.dmConversations],
    ({ pageParam = 0 }) => fetchConversations({ offset: pageParam }),
    {
      // LimitOffset: derive the next offset from how many rows we've pulled so far.
      getNextPageParam: (lastPage, pages) => {
        if (!lastPage || !lastPage.next) return undefined;
        const loaded = pages.reduce(
          (sum, page) => sum + (page?.results?.length ?? 0),
          0
        );
        return loaded;
      },
      refetchOnWindowFocus: false,
      ...config,
    }
  );

export const useConversation = (conversationId, config = {}) =>
  useQuery(
    [QUERY_KEYS.dmConversations, conversationId],
    () => fetchConversation(conversationId),
    {
      enabled: !!conversationId,
      refetchOnWindowFocus: false,
      ...config,
    }
  );

export const useMessages = (conversationId, config = {}) =>
  useInfiniteQuery(
    [QUERY_KEYS.dmMessages, conversationId],
    ({ pageParam = null }) => fetchMessages(conversationId, { cursor: pageParam }),
    {
      enabled: !!conversationId,
      // Cursor: the opaque `cursor` query param off the `next` URL pages older.
      getNextPageParam: (lastPage) => extractCursor(lastPage?.next) || undefined,
      refetchOnWindowFocus: false,
      ...config,
    }
  );

/**
 * A fresh idempotency key for a new DM send. A **retry** must NOT call this —
 * it reuses the failed message's existing `client_id` (DM-CM3) so the backend's
 * `(conversation, sender, client_id)` uniqueness dedupes a dropped-response
 * resend into the same row instead of persisting a duplicate.
 */
export const newClientId = () => uuidv4();

/**
 * Send a wheel-share message into a conversation. Variables:
 * `{ conversationId, wheel }`. On success invalidates that thread + the
 * conversation list so the shared-wheel message and the list preview refresh.
 */
export const useShareWheelToChat = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ conversationId, wheel }) =>
      shareWheelToConversation(conversationId, wheel, newClientId()),
    {
      onSuccess: (_data, { conversationId }) => {
        queryClient.invalidateQueries([QUERY_KEYS.dmMessages, conversationId]);
        queryClient.invalidateQueries([QUERY_KEYS.dmConversations]);
      },
    }
  );
};

export const useSendMessage = (conversationId) => {
  const queryClient = useQueryClient();
  const messagesKey = [QUERY_KEYS.dmMessages, conversationId];
  // Variables are `{ body, attachment, clientId }`. `clientId` is the STABLE
  // idempotency key supplied by the caller — `newClientId()` for a fresh send,
  // or the failed bubble's own `client_id` on a Retry (DM-CM3). `attachment`
  // (D22 / GR-5, optional) is a raw `File`.
  return useMutation(
    ({ body, attachment, clientId }) =>
      sendMessage(conversationId, body, clientId || newClientId(), attachment),
    {
      // Optimistic "Sending" bubble (deck 03): drop a faded pending message into
      // the thread immediately, before the server acks. MessageBubble renders it
      // from `status: "sending"`. An attachment renders from a local blob: URL
      // (`attachment.uploading: true`) until the real, server-hosted image
      // replaces it on refetch. On success the invalidate below refetches the
      // real (server-authored) row in its place; on error it flips to the
      // "Not delivered · Retry" state (deck 04) rather than rolling back. A
      // Retry (same clientId) revives the existing bubble in place — see
      // upsertOptimisticMessage — rather than adding a duplicate.
      onMutate: async ({ body, attachment, clientId }) => {
        const cid = clientId || newClientId();
        await queryClient.cancelQueries(messagesKey);
        const previous = queryClient.getQueryData(messagesKey);
        // A Retry already has a bubble in the cache for this clientId; only a
        // fresh send needs a new local blob URL (the retried bubble keeps its
        // own). This also avoids leaking a blob on every retry click.
        const isRetry =
          !!previous &&
          Array.isArray(previous.pages) &&
          previous.pages.some((page) =>
            (page?.results || []).some((m) => m && m.client_id === cid)
          );
        const localAttachmentUrl =
          attachment && !isRetry ? URL.createObjectURL(attachment) : null;
        const optimistic = {
          id: cid,
          client_id: cid,
          body: body || "",
          is_mine: true,
          status: "sending",
          created_at: new Date().toISOString(),
          attachment: attachment
            ? { url: localAttachmentUrl, content_type: attachment.type, uploading: true }
            : null,
          // Kept ONLY client-side so a failed send's Retry can resubmit the same
          // file — never sent to the server (see MessageSerializer's real shape).
          _pendingFile: attachment || null,
        };
        queryClient.setQueryData(messagesKey, (old) =>
          upsertOptimisticMessage(old, optimistic)
        );
        return { previous, clientId: cid, localAttachmentUrl };
      },
      onError: (_err, _vars, context) => {
        if (!context) return;
        queryClient.setQueryData(messagesKey, (old) =>
          markMessageStatus(old, context.clientId, "failed")
        );
      },
      onSuccess: (_data, _vars, context) => {
        if (context?.localAttachmentUrl) URL.revokeObjectURL(context.localAttachmentUrl);
        // Refresh the thread (real bubble replaces the optimistic one) and the
        // list (last-message preview + ordering).
        queryClient.invalidateQueries(messagesKey);
        queryClient.invalidateQueries([QUERY_KEYS.dmConversations]);
      },
    }
  );
};

export const useCreateConversation = () => {
  const queryClient = useQueryClient();
  return useMutation((recipientId) => createConversation(recipientId), {
    onSuccess: () => {
      // A get-or-created conversation should surface in the list (new row, or
      // bumped ordering) — refresh it so /messages is consistent on arrival.
      queryClient.invalidateQueries([QUERY_KEYS.dmConversations]);
    },
  });
};

/**
 * QA-1006-shape fix (see `patchConversationUnread`'s doc comment in
 * Chat.helpers.js): zero the LIST and DETAIL entries synchronously in
 * `onMutate` so the navbar badge clears the instant the thread is opened,
 * instead of waiting on the `onSuccess` invalidate's background-refetch
 * round-trip (measured live as a multi-second visible lag). Rolls back both
 * entries on error since this is now optimistic, not just a post-hoc sync.
 */
export const useMarkConversationRead = () => {
  const queryClient = useQueryClient();
  return useMutation((conversationId) => markConversationRead(conversationId), {
    onMutate: async (conversationId) => {
      const listKey = [QUERY_KEYS.dmConversations];
      const detailKey = [QUERY_KEYS.dmConversations, conversationId];
      await Promise.all([
        queryClient.cancelQueries(listKey),
        queryClient.cancelQueries(detailKey),
      ]);
      const previousList = queryClient.getQueryData(listKey);
      const previousDetail = queryClient.getQueryData(detailKey);
      queryClient.setQueryData(listKey, (old) =>
        patchConversationUnread(old, conversationId, 0)
      );
      if (previousDetail !== undefined) {
        queryClient.setQueryData(detailKey, (old) =>
          old ? { ...old, unread_count: 0 } : old
        );
      }
      return { previousList, previousDetail, conversationId };
    },
    onError: (_err, conversationId, context) => {
      if (!context) return;
      queryClient.setQueryData([QUERY_KEYS.dmConversations], context.previousList);
      if (context.previousDetail !== undefined) {
        queryClient.setQueryData(
          [QUERY_KEYS.dmConversations, conversationId],
          context.previousDetail
        );
      }
    },
    onSuccess: (_data, conversationId) => {
      // Reconciles against server truth (e.g. a message that arrived between
      // the optimistic zero above and the response landing).
      queryClient.invalidateQueries([QUERY_KEYS.dmConversations]);
      queryClient.invalidateQueries([QUERY_KEYS.dmConversations, conversationId]);
    },
  });
};

/**
 * Mute / unmute (C4). The server owns `muted` (on the conversation) and ECHOES
 * the committed value ({ data: { muted } }), so we bind to that echo directly.
 *
 * QA-1006: a plain prefix-invalidate is NOT enough for the thread-header menu.
 * Thread.jsx reads the single-conversation detail query `[dmConversations, id]`
 * (useConversation), and `invalidateQueries([dmConversations])` only marks that
 * active query STALE — it does not reliably REFETCH-and-apply it. Measured on
 * the deployed bundle (main.f1097468.js, 2026-08-24): after muting from the
 * thread header the server committed muted:true and the detail GET even fired,
 * yet the `[dmConversations, "18"]` cache stayed muted:false until a full page
 * reload, so the header never flipped Mute -> Unmute and Unmute was unreachable.
 * The MiniDmDock escaped this only because it reads the LIST query instead.
 *
 * Fix: write the server-confirmed `muted` straight into the detail entry Thread
 * reads (setQueryData — synchronous, no refetch, no read-after-write race), so
 * every reader of `[dmConversations, id]` reflects committed state at once.
 *
 * The LIST query (`[dmConversations]`) had the SAME gap: only
 * `invalidateQueries` touched it, which marks it stale but does not reliably
 * refetch-and-apply — so the list-row kebab (Mute/Unmute label), the corner
 * dock's header (which reads its active conversation off this SAME list, not
 * the detail query), AND `notifiableUnreadCount` (which filters on `c.muted`
 * read from this list) all kept the PRE-toggle `muted` value until a full
 * reload: the row looked desynced ("still says Mute" / re-clicking felt
 * disabled because nothing visibly changed) and a just-muted conversation's
 * unread kept counting toward the navbar badge. Patch it the same way, with
 * `patchConversationMuted`. `isLoading` guards double-submit.
 */
const useMuteMutation = (fn) => {
  const queryClient = useQueryClient();
  return useMutation((conversationId) => fn(conversationId), {
    onSuccess: (response, conversationId) => {
      // muteConversation/unmuteConversation resolve the response body
      // `{ success, message, status_code, data: { muted } }` (see their defs).
      const confirmedMuted = response && response.data && response.data.muted;
      const detailKey = [QUERY_KEYS.dmConversations, conversationId];
      // Only patch an entry that actually exists (Thread's detail query). The
      // dock has no detail entry for its active id and is untouched here — its
      // list read is patched directly below.
      if (
        typeof confirmedMuted === "boolean" &&
        queryClient.getQueryData(detailKey) !== undefined
      ) {
        queryClient.setQueryData(detailKey, (old) =>
          old ? { ...old, muted: confirmedMuted } : old
        );
      }
      if (typeof confirmedMuted === "boolean") {
        queryClient.setQueryData([QUERY_KEYS.dmConversations], (old) =>
          patchConversationMuted(old, conversationId, confirmedMuted)
        );
      }
      // Still refresh the conversation LIST so anything else it feeds re-reads.
      queryClient.invalidateQueries([QUERY_KEYS.dmConversations]);
    },
  });
};

export const useMuteConversation = () => useMuteMutation(muteConversation);

export const useUnmuteConversation = () => useMuteMutation(unmuteConversation);

/**
 * Leave / delete a conversation (I6). On success the thread drops off the
 * caller's list, so invalidate the conversation list. `isLoading` guards
 * double-submit while the confirm dialog is open.
 */
export const useLeaveConversation = () => {
  const queryClient = useQueryClient();
  return useMutation((conversationId) => leaveConversation(conversationId), {
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.dmConversations]);
    },
  });
};

/**
 * Edit / delete own message (1:1 polish). No optimistic cache surgery here —
 * unlike send, these aren't on the perceived-latency critical path, so the
 * simpler correct thing is: mutate, then invalidate on success and let the
 * caller (MessageBubble) hold its own local pending/error UI state. Both
 * invalidate the conversation list too, since an edited/deleted LAST message
 * changes its preview row (`MessagePreviewSerializer` mirrors the same
 * tombstone rule server-side).
 */
export const useEditMessage = (conversationId) => {
  const queryClient = useQueryClient();
  const messagesKey = [QUERY_KEYS.dmMessages, conversationId];
  return useMutation(({ messageId, body }) => editMessage(messageId, body), {
    onSuccess: () => {
      queryClient.invalidateQueries(messagesKey);
      queryClient.invalidateQueries([QUERY_KEYS.dmConversations]);
    },
  });
};

export const useDeleteMessage = (conversationId) => {
  const queryClient = useQueryClient();
  const messagesKey = [QUERY_KEYS.dmMessages, conversationId];
  return useMutation((messageId) => deleteMessage(messageId), {
    onSuccess: (_data, messageId) => {
      queryClient.invalidateQueries(messagesKey);
      // QA-1006 shape (see useMuteMutation above): the conversation-list row
      // reads the LIST query directly, which stays mounted behind the thread
      // and so SHOULD pick up a background refetch — but MEASURED (live
      // bundle main.ff3172c5.js, gordon-21) the row kept showing the deleted
      // message's raw body until a full reload. Write the confirmed tombstone
      // straight into the list cache entry ConversationRow reads so the
      // preview flips synchronously; keep the invalidate as a normal refresh
      // for everything else the row shows (ordering, unread count, etc).
      queryClient.setQueryData([QUERY_KEYS.dmConversations], (old) =>
        tombstoneConversationLastMessage(old, conversationId, messageId)
      );
      queryClient.invalidateQueries([QUERY_KEYS.dmConversations]);
    },
  });
};

/**
 * BUG #919 — toggle the caller's reaction on a message. Variables: `{ message,
 * emoji }` (the full message object, so `applyReactionToggle` can read its
 * current `reactions`). Optimistic: the chip updates instantly via
 * `patchMessageReactions`, exactly like SegmentReactions' overlay — and rolls
 * back to the pre-toggle cache on error rather than leaving a chip the server
 * never actually recorded. BE's reaction endpoints now exist (Backend
 * `messaging/api/v1/views.py` `MessageReactionView`); see the swap comment
 * below for why the "add" branch is a single POST, not a DELETE+POST pair.
 */
export const useToggleMessageReaction = (conversationId) => {
  const queryClient = useQueryClient();
  const messagesKey = [QUERY_KEYS.dmMessages, conversationId];
  return useMutation(
    async ({ message, emoji }) => {
      const { action, removedEmoji, addedEmoji } = applyReactionToggle(
        message?.reactions,
        emoji
      );
      if (action === "remove") {
        return removeMessageReaction(message.id, removedEmoji);
      }
      // A swap (different emoji already mine) is a single POST — BE's
      // `set_reaction` (messaging/services/messaging.py) already swaps the
      // caller's reaction atomically in one DB transaction (one row per
      // message+user). Calling DELETE(old) then POST(new) here would split
      // that atomic swap into two independent round trips: if the DELETE
      // committed but the POST then failed, the server would be left with
      // NO reaction while the optimistic-rollback cache still showed the
      // old one — a chip the user sees that was never actually persisted.
      return addMessageReaction(message.id, addedEmoji);
    },
    {
      onMutate: async ({ message, emoji }) => {
        await queryClient.cancelQueries(messagesKey);
        const previous = queryClient.getQueryData(messagesKey);
        const { next } = applyReactionToggle(message?.reactions, emoji);
        queryClient.setQueryData(messagesKey, (old) =>
          patchMessageReactions(old, message.id, next)
        );
        return { previous };
      },
      onError: (_err, _vars, context) => {
        if (context?.previous) {
          queryClient.setQueryData(messagesKey, context.previous);
        }
      },
      onSuccess: () => {
        queryClient.invalidateQueries(messagesKey);
      },
    }
  );
};

/**
 * ISP-9999 (Gordon #chatmedia-save): "Save to Media Library" mutation. No
 * optimistic cache write and no invalidation — this never changes the
 * message/thread itself (the sender's copy and the message row are
 * untouched), it only adds a new object to the caller's own Media Library,
 * which is a different page/query entirely.
 */
export const useSaveMessageToLibrary = () =>
  useMutation((messageId) => saveMessageAttachmentToLibrary(messageId));

/* ----- user blocking (#18 Gap #1) -----
 *
 * Guarded by the build flag at every call site: with USER_BLOCKING_ENABLED off
 * these hooks are never invoked, so no request is issued and behaviour is
 * byte-identical to today.
 *
 * On success both invalidate the conversation queries rather than patching the
 * cache by hand: the server owns `is_blocked` (and, once blocking is on, the
 * follow graph the block just severed), so re-reading it is the only way to be
 * right. Callers use `isLoading` for double-submit guarding.
 */
export const blockUser = (userId) =>
  API.post(API_PATH.USER_BLOCK(userId)).then((res) => res.data);

export const unblockUser = (userId) =>
  API.post(API_PATH.USER_UNBLOCK(userId)).then((res) => res.data);

/**
 * Block DIRECTION (BUG-usman-chat-U6/U7).
 *
 * `conversation.is_blocked` is deliberately SYMMETRIC server-side
 * (messaging/api/v1/serializers.py `get_is_blocked` -> `not can_interact(...)`),
 * so both people in a blocked pair receive the identical payload. MEASURED on
 * app-stg 2026-08-19 (A=12146 blocked B=12145, conversation 28): both sides read
 * `is_blocked: true` and the payload carries NO direction field — which is why
 * the blocked person saw "You blocked X" and was offered an Unblock that the
 * server correctly refuses to honour (it only ever deletes the CALLER's own
 * block row, so B's unblock 200s as a no-op).
 *
 * Direction already exists on a separate endpoint:
 *   GET /api/v1/user/{id}/block-status/ -> { is_blocked, blocked_by_me, blocked_me }
 * (MEASURED same run: as A `blocked_by_me:true`; as B `blocked_me:true`.)
 * This hook reads it so the UI can tell the two states apart without any BE
 * change. Fetched only when a caller actually needs it (a blocked thread).
 */
export const fetchBlockStatus = (userId) =>
  API.get(API_PATH.USER_BLOCK_STATUS(userId)).then((res) => res.data?.data ?? res.data);

export const useBlockStatus = (userId, config = {}) =>
  useQuery(
    [QUERY_KEYS.dmBlockStatus, userId],
    () => fetchBlockStatus(userId),
    {
      enabled: userId != null,
      refetchOnWindowFocus: false,
      ...config,
    }
  );

const useBlockMutation = (fn) => {
  const queryClient = useQueryClient();
  return useMutation((userId) => fn(userId), {
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.dmConversations]);
      // Direction can only have changed here, so re-read it rather than
      // patching the cache by hand (the server owns it).
      queryClient.invalidateQueries([QUERY_KEYS.dmBlockStatus]);
    },
  });
};

export const useBlockUser = () => useBlockMutation(blockUser);

export const useUnblockUser = () => useBlockMutation(unblockUser);
